import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import csLocale from '@fullcalendar/core/locales/cs';
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list';
import * as tempusDominus from '@eonasdan/tempus-dominus';


import naja from "naja";

document.addEventListener('DOMContentLoaded', () => {
    const calendar = new FullCalendar();
    calendar.init();

    naja.addEventListener('complete', (event) => {
        calendar.init();
        if (event.detail.payload && (event.detail.payload.snippets || event.detail.payload.postGet)) {
            const snippets = event.detail.payload.snippets || {};
            // Pokud je v payloadu postGet nebo snippet s kalendářem, musíme refrešnout i instance, které se nepřekreslily celé
            document.querySelectorAll('.calendar').forEach(calendarEl => {
                if (calendarEl.fullCalendarInstance) {
                    calendarEl.fullCalendarInstance.refetchEvents();
                }
            });
        }
    });

});

class FullCalendar {
    storageKey(calendarEl) {
        return 'fc:view:' + (calendarEl.getAttribute('data-eventsSourceLink') || 'default');
    }

    loadState(calendarEl) {
        try {
            return JSON.parse(localStorage.getItem(this.storageKey(calendarEl)) || '{}');
        } catch (e) {
            return {};
        }
    }

    saveState(calendarEl, state) {
        localStorage.setItem(this.storageKey(calendarEl), JSON.stringify(state));
    }
    init() {
        const calendarElements = document.querySelectorAll('.calendar');
        let self = this;
        calendarElements.forEach(calendarEl => {
            if (calendarEl.getAttribute('data-loaded') === 'true') {
                return;
            }
            calendarEl.setAttribute('data-loaded', 'true');
            const eventsSourceLink = calendarEl.getAttribute('data-eventsSourceLink');
            if (eventsSourceLink === null) {
                const message = 'Missing attribute data-eventsSourceLink on calendar element!';
                console.error(message, calendarEl);
                calendarEl.innerHTML = "<div class='alert alert-danger'>Chybná konfigurace kalendáře, více informací v JavaScript konzoli.</div>"
                return;
            }
            const state = this.loadState(calendarEl);
            const calendar = new Calendar(calendarEl, {
                plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, bootstrap5Plugin],
                initialView: state.view || 'dayGridMonth',
                initialDate: state.date || undefined,
                locale: csLocale,
                themeSystem: 'bootstrap5',
                editable: true,
                selectable: true,
                height: 'auto',
                contentHeight: 'auto',
                expandRows: true,
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridFourDay,timeGridDay,listMonth'
                },
                views: {
                    timeGridFourDay: {
                        type: 'timeGrid',
                        duration: { days: 4 },
                        buttonText: '4 dny'
                    }
                },
                eventSources: {
                    url: eventsSourceLink
                },
                datesSet: (arg) => {
                    // ulozeni aktualniho pohledu a data do localStorage, aby se pri navratu zachoval
                    this.saveState(calendarEl, {
                        view: arg.view.type,
                        date: arg.view.currentStart.toISOString()
                    });
                },
                eventsSet: function () {
                    document.querySelectorAll('.fc-toolbar button').forEach(function (button) {
                        button.setAttribute('data-loading-indicator', 'false');
                    });
                },
                eventDidMount: function (info) {
                    const bg = info.el.style.backgroundColor || info.event.backgroundColor;
                    if (!bg) return;
                    const m = bg.match(/\d+/g);
                    if (!m || m.length < 3) return;
                    const [r, g, b] = m.map(Number);
                    // relativní luminance (WCAG)
                    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                    if (luminance > 160) {
                        info.el.style.color = '#000000';
                        info.el.querySelectorAll('.fc-event-title, .fc-event-time, .fc-list-event-title').forEach(el => {
                            el.style.color = '#000000';
                        });
                    }
                },
                dateClick: function (info) {
                    self.showDay(this.el, info.dateStr);
                },
                eventClick: function (info) {
                    self.showEventDetail(this.el, info.event.id);
                },
                eventDrop: function (info) {
                    self.handleEventMove(this.el, info.event, info.revert);
                }
            });
            calendar.render();
            calendarEl.fullCalendarInstance = calendar;
        });
    }

    showDay(calendarEl, date) {
        let link = calendarEl.getAttribute('data-showDayLink');
        if (link === null) {
            console.error('Missing attribute data-showDayLink on calendar element!');
            return;
        }

        if (!link.includes('--date--')) {
            console.error('data-showDayLink on calendar element must contain placeholder --date--, which will be replaced with selected date!');
            return;
        }

        link = link.replace('--date--', date);
        naja.makeRequest('GET', link);
    }

    showEventDetail(calendarEl, eventId) {
        let link = calendarEl.getAttribute('data-showEventDetailLink');
        if (link === null) {
            console.error('Missing attribute data-showEventDetailLink on calendar element!');
            return;
        }

        if (!link.includes('--id--')) {
            console.error('data-showEventDetailLink on calendar element must contain placeholder --id--, which will be replaced with selected eventId!');
            return;
        }

        link = link.replace('--id--', eventId);
        naja.makeRequest(
            'GET', link, {
            isModal: true
        }
        );
    }

    handleEventMove(calendarEl, event, revertMoveEvent) {
        let link = calendarEl.getAttribute('data-moveEventLink');

        if (link === null) {
            console.error('Missing attribute updateEventLink in event data or extendedProps is not defined!');
            revertMoveEvent();
            return;
        }

        if (!link.includes('--id--') || !link.includes('--from--') || !link.includes('--to--')) {
            console.error('updateEventLink must contain placeholders --id-- and --date-- to be replaced with event id and new date!');
            revertMoveEvent();
            return;
        }

        // Lokální čas bez převodu na UTC – toISOString() by posunul wall-clock čas o offset časové zóny (CEST +2 h)
        const toLocalIso = (date) => {
            const pad = (n) => String(n).padStart(2, '0');
            return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
                + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds());
        };

        link = link.replace('--id--', event.id)
            .replace('--from--', toLocalIso(event.start))
            .replace('--to--', toLocalIso(event.end));

        naja.makeRequest('GET', link, null, { history: false }).then(function (response) {
            if (response.result !== 'success') {
                console.log('Event move failed, reverting...');
                revertMoveEvent(); // Revert the event if the result is not 'success'
            }
        }).catch(function (error) {
            console.error('Event move failed due to error', error);
            revertMoveEvent(); // Revert the event in case of an error
            alert('Přesunutí události selhalo. Obnovte stránku a zkuste to prosím znovu.');
        });
    }

    populateForm(date) {
        const fromInput = document.querySelector('#frm-tabs-tabContent-naplanovat-tabControl-editForm-from');
        const toInput = document.querySelector('#frm-tabs-tabContent-naplanovat-tabControl-editForm-to');

        if (fromInput && toInput) {
            // Format date to dd.mm.yyyy (Czech format without spaces, typical for this app's parsing)
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const dateString = `${day}.${month}.${year}`;

            fromInput.value = dateString + ' 08:00';
            toInput.value = dateString + ' 16:00'; // User requested 16:00 in previous step

            fromInput.dispatchEvent(new Event('change'));
            toInput.dispatchEvent(new Event('change'));
        }
    }
}
